import { Component, OnInit } from '@angular/core';
import { ProfileSearchResult } from '../../data/ProfileSearchResult';
import { EntitlementService } from '../../services/entitlement.service';
import { CardholderService } from '../../services/cardholder.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AdjustmentReason } from '../../merchants/merchant-adjustments/merchant-adjustments.component';
import { CustomerAccount } from '../../data/CustomerAccount';
import { MessageService } from '../../services/message.service';
import { CardholderDetails } from '../../data/CardholderDetails';
import { Identity } from '../../Identity';
import { Card } from '../../data/Card';
import { NCConstants } from '../../data/NCConstants';

@Component({
  selector: 'app-cardholder-adjustments',
  templateUrl: './cardholder-adjustments.component.html',
  styleUrls: ['./cardholder-adjustments.component.css']
})
export class CardholderAdjustmentsComponent implements OnInit {
  cardholder: ProfileSearchResult;

  srcAccts: any;
  adjustAmt: number;
  adjustReason: string;
  srcAcct: number;
  desAcct: number;
  xferAmt: number;
  transferSummary: any;
  adjustmentReasons: AdjustmentReason[];
  financialAdjustmentsForm: UntypedFormGroup;
  balanceTransferForm: UntypedFormGroup;
  commitButtonSpinnerString = '';
  applyBalanceButtonSpinnerString = '';
  saveBtnSpinner = 'fa fa-spinner fa-spin';
  identity: Identity;
  customerAccount: CustomerAccount;
  startDate: Date;
  endDate: Date;
  transactions: any[] = [];
  accountType: string;
  chipOnly: boolean;
  cardsAccount: Card[];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private entitlementService: EntitlementService,
    private cardHolderService: CardholderService,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    this.cardholder = CardholderService.retrieveCardholder();
    this.loadPageData();
  }

  showNewCardholder(newCardholder: ProfileSearchResult) {
    CardholderService.storeCardHolder(newCardholder);
    this.cardholder = newCardholder;
    this.loadPageData();
  }

  loadPageData() {
    this.endDate = new Date();
    this.startDate = new Date(
      this.endDate.getFullYear(),
      this.endDate.getMonth() - 3,
      1
    );
    this.cardHolderService.loadAdjustments().subscribe(
      (data: AdjustmentReason[]) => {
        this.adjustmentReasons = data;
        this.adjustReason = this.adjustmentReasons[0].COLUMN_VALUE;
        (<UntypedFormGroup>this.financialAdjustmentsForm).patchValue(
          { adjustReason: this.adjustReason },
          { onlySelf: true }
        );
      },
      () => {
        this.messageService.error('Could not load Adjustment Reasons');
      }
    );

    this.cardHolderService
      .getCardholderDetails(this.cardholder.payrollAccount)
      .subscribe(
        (data: CardholderDetails) => {
          this.identity = data.identity;
          this.cardsAccount = data.cardList;
          this.customerAccount = data.customerAccounts[0];
          this.setAccountNumbers(data.cardList);
        },
        error => {
          this.messageService.error(error.message);
        },
        () => {
          this.checkChipOnly();
          this.cardHolderService.chipDetails(this.chipOnly);
        }
      );

    this.initForm();
  }

  initForm() {
    this.financialAdjustmentsForm = this.formBuilder.group({
      adjustReason: [this.adjustReason, []],
      adjustAmt: [
        this.adjustAmt,
        [Validators.pattern(/^(\d{1,8}\.\d{1,2}|\d+)$/)]
      ]
    });

    this.balanceTransferForm = this.formBuilder.group({
      srcAcct: [this.srcAcct, []],
      desAcct: [this.desAcct, [Validators.pattern(/^[0-9]{3,16}$/)]],
      xferAmt: [this.xferAmt, [Validators.pattern(/^(\d{1,8}\.\d{1,2}|\d+)$/)]]
    });
  }

  get finAdjFormHandle() {
    return this.financialAdjustmentsForm.controls;
  }
  get balTransFormHandle() {
    return this.balanceTransferForm.controls;
  }

  hasEntitlements(entitlement) {
    return this.entitlementService.hasEntitlement(entitlement);
  }

  applyAdjustment(proceed: boolean) {
    if (proceed) {
      this.commitButtonSpinnerString = this.saveBtnSpinner;
      this.cardHolderService
        .applyAdjustment(
          this.cardholder.payrollAccount,
          this.financialAdjustmentsForm.value['adjustReason'],
          this.financialAdjustmentsForm.value['adjustAmt']
        )
        .subscribe(
          () => {
            this.messageService.success('Balance Adjusted Successfully.');
            (<UntypedFormGroup>this.financialAdjustmentsForm).patchValue(
              { adjustAmt: '' },
              { onlySelf: true }
            );
            this.financialAdjustmentsForm.controls[
              'adjustAmt'
            ].markAsUntouched();
            (<UntypedFormGroup>this.financialAdjustmentsForm).patchValue(
              { adjustReason: this.adjustmentReasons[0].COLUMN_VALUE },
              { onlySelf: true }
            );
            this.transferSummary = null;
          },
          () => {
            this.messageService.error('Transfer Failed');
          },
          () => {
            this.commitButtonSpinnerString = '';
          }
        );
    }
  }

  balanceTransferClicked() {
    // Validation helper
    Object.keys(this.balanceTransferForm.controls).forEach(field => {
      const control = this.balanceTransferForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  adjustmentTransferClicked() {
    // Validation helper
    Object.keys(this.financialAdjustmentsForm.controls).forEach(field => {
      const control = this.financialAdjustmentsForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  applyBalance(proceed: boolean) {
    if (proceed) {
      this.applyBalanceButtonSpinnerString = this.saveBtnSpinner;
      this.cardHolderService
        .balanceTransfer(
          this.balanceTransferForm.value['srcAcct'],
          this.balanceTransferForm.value['desAcct'],
          this.balanceTransferForm.value['xferAmt']
        )
        .subscribe(
          data => {
            this.applyBalanceButtonSpinnerString = '';
            this.messageService.success('Balance Transfered Successfully.');
            (<UntypedFormGroup>this.balanceTransferForm).patchValue(
              { srcAcct: this.srcAccts[0].value },
              { onlySelf: true }
            );
            (<UntypedFormGroup>this.balanceTransferForm).patchValue(
              { desAcct: '' },
              { onlySelf: true }
            );
            this.balanceTransferForm.controls['desAcct'].markAsUntouched();
            (<UntypedFormGroup>this.balanceTransferForm).patchValue(
              { xferAmt: '' },
              { onlySelf: true }
            );
            this.balanceTransferForm.controls['xferAmt'].markAsUntouched();
            this.balanceTransferForm.markAsUntouched();
            this.transferSummary = JSON.parse(data);
            this.transactions = null;
          },
          error => {
            this.applyBalanceButtonSpinnerString = '';
            if (error.status === NCConstants.HTTP_STATUS_INSUFFICIENT_STORAGE) {
              this.messageService.error('Transfer Failed ' + error.error);
            } else {
              this.messageService.error('Transfer Failed ' + error.message);
            }
          }
        );
    }
  }

  setAccountNumbers(cardList: Card[]) {
    let card = cardList[0];
    const jsonArr = [];
    let masterCard;
    if (card.payrollAccount) {
      jsonArr.push({
        value: card.payrollAccount,
        label: 'Strip - ' + card.payrollAccount
      });
    }

    for (let j = 0; j < cardList.length; j++) {
      card = cardList[j];
      if (card.chipAccount) {
        masterCard = card.masterCard;
      }
      if (card.masterCard != null && card.masterCard.toString().length > 12) {
        masterCard = card.masterCard.toString().substring(12);
      }
      jsonArr.push({
        value: card.chipAccount,
        label: 'Chip - ' + card.chipAccount + '-Card-' + masterCard
      });
    }
    this.srcAccts = jsonArr;
    this.srcAcct = this.srcAccts[0].value;
    (<UntypedFormGroup>this.balanceTransferForm).patchValue(
      { srcAcct: this.srcAccts[0].value },
      { onlySelf: true }
    );
    this.loadTransactionHistory();
  }

  loadTransactionHistory() {
    const startDateString =
      this.startDate.getMonth() +
      1 +
      '/' +
      this.startDate.getDate() +
      '/' +
      this.startDate.getFullYear();
    const endDateString =
      this.endDate.getMonth() +
      1 +
      '/' +
      this.endDate.getDate() +
      '/' +
      this.endDate.getFullYear();

    if (this.srcAcct === this.srcAccts[0].value) {
      this.cardHolderService
        .getAdjustmentTransHistory(
          startDateString,
          endDateString,
          this.srcAccts[0].value,
          'payroll'
        )
        .subscribe(data => {
          this.transactions = data['transactions'];
          this.accountType = 'Strip';
        });
    } else {
      this.cardHolderService
        .getAdjustmentTransHistory(
          startDateString,
          endDateString,
          this.srcAcct,
          'chip'
        )
        .subscribe(data => {
          this.transactions = data['transactions'];
          this.accountType = 'Chip';
        });
    }
  }

  checkChipOnly() {
    const index = this.cardsAccount.findIndex(x => x.type !== 'CHIP');
    this.chipOnly = index < 0;
  }
}
